import React, { useState } from 'react';
import './Privilege.scss';
import { Helmet } from 'react-helmet';
import privilegeHero from '../assets/images/pressto-privilege-background.jpeg';

const Privilege = () => {
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const customerName = formData.get('name');

    setIsLoading(true);
    setStatus('Please wait, we are reserving your request.');

    try {
      const response = await fetch('https://www.presstoindia.com/api/send-reservation.php', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      let result;
      try {
        result = await response.json();
      } catch (e) {
        throw new Error('Invalid server response.');
      }

      if (!response.ok || !result.ok) {
        throw new Error(result.message || 'Reservation could not be sent.');
      }

      setStatus(`Thank you, ${customerName}. Our team will reach out to you soon.`);
      e.target.reset();
    } catch (error) {
      console.error('Reservation error:', error);
      setStatus('Sorry, we could not send your reservation right now. Please call 1800229199 or WhatsApp 1800229199.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="privilege-page">
      <Helmet>
        <title>Pressto Privilege Reservation</title>
        <meta name="description" content="Reserve your Pressto Privilege customer request with name and registered contact number." />
        <link rel="canonical" href="https://www.presstoindia.com/privilege/" />
        <meta property="og:title" content="Pressto Privilege Reservation" />
        <meta property="og:description" content="Reserve your Pressto Privilege customer request and the Pressto team will reach out soon." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.presstoindia.com/privilege/" />
        <meta property="og:image" content="https://www.presstoindia.com/assets/images/pressto-privilege-background.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pressto Privilege Reservation" />
        <meta name="twitter:description" content="Reserve your Pressto Privilege customer request and the Pressto team will reach out soon." />
        <meta name="twitter:image" content="https://www.presstoindia.com/assets/images/pressto-privilege-background.jpeg" />
      </Helmet>

      <main>
        <section className="hero" aria-labelledby="page-title">
          <div className="hero-shell">
            <div
              className="privilege-visual"
              role="img"
              aria-label="Pressto Privilege exclusive benefits: 24 hour cleaning and pressing, daily home pickup and drop, Rs 60 wash and fold, and 20 percent savings on shoe and bag care."
            >
              <img
                className="privilege-visual__photo"
                src={privilegeHero}
                alt=""
                width={960}
                height={1280}
                decoding="async"
              />
            </div>

            <div className="reservation-column">
              <div className="hero-copy">
                <span className="eyebrow">Pressto Privilege</span>
                <h1 id="page-title">Reserve yourself for Privilege</h1>
                <p>
                  Share your details and the Pressto team will connect with you to confirm your
                  Privilege customer reservation.
                </p>

                <div className="contact-strip" aria-label="Contact Pressto">
                  <a className="contact-pill whatsapp" href="https://wa.me/911800229199" target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor" d="M12.04 2C6.58 2 2.13 6.32 2.13 11.64c0 1.7.46 3.36 1.33 4.81L2 22l5.72-1.46a10.13 10.13 0 0 0 4.32.96c5.47 0 9.91-4.33 9.91-9.66C21.95 6.32 17.51 2 12.04 2Zm5.78 13.58c-.24.65-1.39 1.24-1.94 1.32-.5.08-1.13.11-1.82-.11-.42-.13-.96-.31-1.65-.61-2.9-1.22-4.78-4.02-4.93-4.2-.14-.18-1.18-1.52-1.18-2.9 0-1.38.75-2.06 1.02-2.34.27-.28.59-.35.79-.35h.56c.18 0 .42-.07.66.5.24.56.84 1.94.91 2.08.07.14.12.31.02.49-.1.18-.15.29-.3.45-.15.17-.31.37-.45.49-.15.14-.31.3-.13.58.18.28.8 1.28 1.72 2.07 1.18 1.02 2.18 1.33 2.49 1.47.31.14.49.12.68-.07.18-.21.78-.88.99-1.18.21-.31.42-.26.71-.16.29.1 1.84.84 2.16.99.31.16.52.24.59.38.07.14.07.78-.17 1.43Z" />
                    </svg>
                    1800229199
                  </a>
                  <a className="contact-pill call" href="tel:1800229199">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.92.33 1.82.62 2.68a2 2 0 0 1-.45 2.11L8 9.78a16 16 0 0 0 6.22 6.22l1.27-1.27a2 2 0 0 1 2.11-.45c.86.29 1.76.5 2.68.62A2 2 0 0 1 22 16.92Z" />
                    </svg>
                    1800229199
                  </a>
                </div>
              </div>

              <form
                className="reservation-card"
                id="reservation"
                onSubmit={handleSubmit}
                aria-labelledby="reservation-title"
              >
                <h2 id="reservation-title">Privilege Reservation</h2>
                <p>Please enter the customer details for confirmation.</p>

                <div className="field">
                  <label htmlFor="customer-name">Name</label>
                  <input id="customer-name" name="name" type="text" autoComplete="name" required />
                </div>

                <div className="field">
                  <label htmlFor="customer-phone">Registered Contact number</label>
                  <input
                    id="customer-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    pattern="[0-9+\-\s]{8,16}"
                    required
                  />
                </div>

                <label className="consent">
                  <input type="checkbox" name="terms" required />
                  <span>I agree to the Terms &amp; Conditions and consent to be contacted by Pressto India.</span>
                </label>

                <div className="spam-check" aria-hidden="true">
                  <label htmlFor="company-website">Website</label>
                  <input id="company-website" name="company_website" type="text" tabIndex="-1" autoComplete="off" />
                </div>

                <button className="submit-button" type="submit" disabled={isLoading}>
                  {isLoading ? 'Processing...' : 'Reserve'}
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
                <p className="form-status" role="status" aria-live="polite">{status}</p>
              </form>
            </div>
          </div>
        </section>

        <section className="terms" aria-labelledby="terms-title">
          <div className="terms-inner">
            <h2 id="terms-title">Terms &amp; Conditions</h2>
            <p className="terms-intro">
              Pressto Privilege is subject to the following customer confirmation terms.
            </p>
            <ul className="terms-list">
              <li>Pressto Privilege is extended to select customers at the sole discretion of Pressto.</li>
              <li>Services are subject to availability, serviceability, and operational feasibility.</li>
              <li>Daily pickup and delivery timings are indicative and may vary due to external factors such as traffic, weather, or operational constraints.</li>
              <li>We endeavour to complete delivery within 24 hours for select items, subject to garment/material condition and operational feasibility.</li>
              <li>Pressto Daily pricing of Rs 60 per piece is introductory price and is subject to change.</li>
              <li>Pressto reserves the right to decline or limit services for certain items based on feasibility or risk.</li>
              <li>Pressto reserves the right to modify, suspend, or withdraw the Privilege service at its discretion, without prior notice.</li>
              <li>By availing Pressto Privilege, customers may receive service-related communication via call, SMS, or digital channels.</li>
              <li>All services are governed by standard company policies.</li>
              <li>Claims, if any, must be raised within the stipulated timeline as per company policy.</li>
            </ul>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <strong>Pressto India Privilege</strong>
        <div className="footer-links">
          <a href="https://wa.me/911800229199" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M12.04 2C6.58 2 2.13 6.32 2.13 11.64c0 1.7.46 3.36 1.33 4.81L2 22l5.72-1.46a10.13 10.13 0 0 0 4.32.96c5.47 0 9.91-4.33 9.91-9.66C21.95 6.32 17.51 2 12.04 2Zm5.78 13.58c-.24.65-1.39 1.24-1.94 1.32-.5.08-1.13.11-1.82-.11-.42-.13-.96-.31-1.65-.61-2.9-1.22-4.78-4.02-4.93-4.2-.14-.18-1.18-1.52-1.18-2.9 0-1.38.75-2.06 1.02-2.34.27-.28.59-.35.79-.35h.56c.18 0 .42-.07.66.5.24.56.84 1.94.91 2.08.07.14.12.31.02.49-.1.18-.15.29-.3.45-.15.17-.31.37-.45.49-.15.14-.31.3-.13.58.18.28.8 1.28 1.72 2.07 1.18 1.02 2.18 1.33 2.49 1.47.31.14.49.12.68-.07.18-.21.78-.88.99-1.18.21-.31.42-.26.71-.16.29.1 1.84.84 2.16.99.31.16.52.24.59.38.07.14.07.78-.17 1.43Z" />
            </svg>
            1800229199
          </a>
          <a href="tel:1800229199">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.92.33 1.82.62 2.68a2 2 0 0 1-.45 2.11L8 9.78a16 16 0 0 0 6.22 6.22l1.27-1.27a2 2 0 0 1 2.11-.45c.86.29 1.76.5 2.68.62A2 2 0 0 1 22 16.92Z" />
            </svg>
            1800229199
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Privilege;
